// @flow

import * as React from 'react';

import get from 'lodash/get';

import type { ProductId, ProductPayload, AddProductArgs } from '@gandi/cart';
import { Base, Svg, Alert, Spinner } from '@gandi/ui';

import { Action } from '@gandi/ui/deprecated';

import { Form, RadioButtonGroupField, SubmissionError } from 'gandi.react-form';

import type { ImageSummary } from '../../../../modules/cloud/resources/images';
import type { RegionSummary } from '../../../../modules/cloud/resources/regions';
import type { TemplateSummary } from '../../../../modules/cloud/resources/templates';
import type { ChildProps } from '../../Presenter';
import { mapErrorsToFields } from '../Form';

export type PresenterProps = {
  ...ChildProps,
  addCartProduct: (body: AddProductArgs) => Promise<any>,
  cartId: string,
  cartIsWorking: boolean,
  datacenters: Array<RegionSummary['datacenters'][number]>,
  datacenterImages: ?Array<ImageSummary>,
  getCart: (cartUUID: ?string) => Promise<any>,
  isFailure: boolean,
  isFetching: boolean,
  isLoaded: boolean,

  // InjectedProps
  // eslint-disable-next-line react/no-unused-prop-types
  images: ?Array<ImageSummary>,
  // eslint-disable-next-line react/no-unused-prop-types
  regions: ?Array<RegionSummary>,
  templates: ?Array<TemplateSummary>,
  // eslint-disable-next-line react/no-unused-prop-types
  isFailureImages: boolean,
  // eslint-disable-next-line react/no-unused-prop-types
  isFailureRegions: boolean,
  // eslint-disable-next-line react/no-unused-prop-types
  isFailureTemplates: boolean,
  // eslint-disable-next-line react/no-unused-prop-types
  isFetchingImages: boolean,
  // eslint-disable-next-line react/no-unused-prop-types
  isFetchingRegions: boolean,
  // eslint-disable-next-line react/no-unused-prop-types
  isFetchingTemplates: boolean,
  // eslint-disable-next-line react/no-unused-prop-types
  isLoadedImages: boolean,
  // eslint-disable-next-line react/no-unused-prop-types
  isLoadedRegions: boolean,
  // eslint-disable-next-line react/no-unused-prop-types
  isLoadedTemplates: boolean,
  updateCartProduct: ({
    productId: ProductId,
    payload: ProductPayload,
  }) => Promise<any>,
};

export type Items = Array<{
  label: React.Node,
  value: string,
  footer?: any,
}>;

type Translations = {
  [key: string]: (nb: number) => string,
  ...
};

const textAlignLeft = { textAlign: 'left' };

export default class Presenter extends React.Component<PresenterProps> {
  translations: Translations;

  constructor(props: PresenterProps) {
    super(props);
    const { translator } = this.props;

    this.translations = {
      cores: count => translator.__('%(count)s CPU cores', { count }),
      ipv4: count => translator.__('%(count)s IP v4', { count }),
      ipv6: count => translator.__('%(count)s IP v6', { count }),
      memory: count =>
        count >= 1024
          ? translator.__('%(count)s GB of RAM', { count: count / 1024 })
          : translator.__('%(count)s MB of RAM', { count }),
      storage: count => translator.__('%(count)s GB of storage', { count }),
    };
  }

  componentDidUpdate(prevProps: PresenterProps) {
    if (
      prevProps.formValues.datacenter !== this.props.formValues.datacenter &&
      this.systemsItems.length
    ) {
      const system = this.systemsItems.filter(
        item => item.value === prevProps.formValues.system,
      )[0];
      this.props.form.change(
        'system',
        system ? system.value : this.systemsItems[0].value,
      );
    }

    if (prevProps.formValues.system !== this.props.formValues.system) {
      this.props.form.change(
        'version',
        this.versionsItems[0] ? this.versionsItems[0].value : undefined,
      );
    }
  }

  UNSAFE_componentWillReceiveProps(nextProps: PresenterProps) {
    if (this.props.product && !nextProps.product) {
      this.props.navigate('/cloud/create', { replace: true });
    }
  }

  get datacentersItems(): Items {
    return this.props.datacenters.map(datacenter => ({
      label: datacenter.name,
      value: datacenter.code,
      footer: <Svg name={(datacenter.region: any)} />,
    }));
  }

  get templatesItems(): ?Items {
    const {
      translator: { formatPrice },
    } = this.props;

    return this.props.templates?.map(template => ({
      label: (
        <span>
          {template.name}
          <br />
          {formatPrice(
            template.price.price_after_taxes,
            template.price.currency,
          )}
        </span>
      ),
      value: template.name,
      footer: (
        <ul style={textAlignLeft}>
          {
            // $FlowFixMe[invalid-computed-prop]
            Object.keys(template[template.type])
              .filter(
                key =>
                  // $FlowFixMe[invalid-computed-prop]
                  !!this.translations[key] && !!template[template.type][key],
              )
              .map(key => (
                <li key={`${key}-${template.name}`}>
                  {
                    // $FlowFixMe[incompatible-call]
                    // $FlowFixMe[invalid-computed-prop]
                    this.translations[key](template[template.type][key])
                  }
                </li>
              ))
          }
        </ul>
      ),
    }));
  }

  get systemsItems(): Items {
    const systems = [
      ...new Set(this.props.datacenterImages?.map(image => image.system)),
    ];
    return systems.map(system => {
      const versions = [
        ...new Set(
          this.props.datacenterImages
            ?.filter(image => image.system === system)
            .map(image => image.version),
        ),
      ];

      const label =
        versions.length > 1
          ? system
          : this.props.translator.__('%(os)s version %(version)s', {
              os: system,
              version: versions[0],
            });

      return {
        label,
        value: system,
        footer: <Svg name={system.toLowerCase()} />,
      };
    });
  }

  get versionsItems(): Items {
    const system = this.props.formValues.system;
    const versions = [
      ...new Set(
        this.props.datacenterImages
          ?.filter(image => image.system === system)
          .map(image => image.version),
      ),
    ];
    return versions.map(version => ({
      label: version,
      value: version,
    }));
  }

  validateDatacenter: (value: string) => string = (value: string) => {
    if (
      typeof value !== 'string' ||
      !value ||
      !this.props.datacenters.filter(datacenter => datacenter.code === value)
        .length
    ) {
      return this.props.translator.__('Please select a datacenter.');
    }

    return '';
  };

  validateTemplate: (value: string) => string = (value: string) => {
    if (
      typeof value !== 'string' ||
      !value ||
      !this.props.templates?.filter(template => template.name === value).length
    ) {
      return this.props.translator.__('Please select a server template.');
    }

    return '';
  };

  validateSystem: (value: string) => string = (value: string) => {
    if (
      typeof value !== 'string' ||
      !value ||
      !this.systemsItems.filter(item => item.value === value).length
    ) {
      return this.props.translator.__(
        'Please select a valid operating system.',
      );
    }

    return '';
  };

  validateVersion: (value: string) => string = (value: string) => {
    if (typeof value !== 'string' || !value) {
      return this.props.translator.__(
        'Please select a valid operating system version.',
      );
    }

    return '';
  };

  handleSubmit: (values: { +[field: string]: mixed, ... }) => any = values => {
    const template = this.props.templates?.filter(
      tpl => tpl.name === values.template,
    )[0];
    if (template) {
      const body: AddProductArgs['productData'] = {
        product_type: 'cloud',
        process: 'add',
        payload: {
          label: template.name,
          isFirstServer: this.props.isFirstServer,
          extra: {
            ...get(this.props.product, 'payload.extra', {}),
            type: template.type,
            template: template.name,
            system: values.system,
            version: values.version,
            datacenter: values.datacenter,
          },
          // $FlowFixMe[invalid-computed-prop]
          [template.type]: {
            // $FlowFixMe[invalid-computed-prop]
            ...template[template.type],
            location: values.datacenter,
            image: this.props.datacenterImages?.filter(
              image =>
                image.version === values.version &&
                image.system === values.system,
            )[0]?.label,
          },
        },
      };

      let productId = get(this.props.product, 'id');
      let promise = Promise.resolve();
      if (productId) {
        promise = this.props.updateCartProduct({
          productId,
          // $FlowFixMe[incompatible-call]
          payload: body.payload,
        });
      } else {
        promise = this.props
          .addCartProduct({
            productData: body,
          })
          .then(response => {
            productId = String(response.data.id);

            const pathname = `/cloud/create/server/${this.props.cartId}/${productId}`;

            this.props.navigate(
              {
                pathname,
                search: this.props.searchParams.toString(),
              },
              { replace: true },
            );

            return response;
          });
      }

      return promise.then(response => {
        const errors = mapErrorsToFields(get(response, 'data.errors', []), [
          'template',
          'datacenter',
          'system',
          'version',
        ]);

        if (errors) {
          throw new SubmissionError(errors);
        } else {
          this.props.getCart(this.props.cartId);
          this.props.navigate(
            `/cloud/create/settings/${this.props.cartId}/${this.props.params.productId}`,
          );
        }
      });
    }

    return undefined;
  };

  render(): null | React.Node {
    const {
      translator: { __ },
    } = this.props;

    if (this.props.isFailure && !this.props.isLoaded) {
      return (
        <Alert
          title={__('Unexpected error')}
          message={__('Unexpected error occurred. Please try again later.')}
          intent="danger"
        />
      );
    }

    if (!this.props.isLoaded && this.props.isFetching) {
      return <Spinner centered />;
    }

    if (this.props.isLoaded) {
      const { formValues } = this.props;

      return (
        <Form
          role="form"
          method="get"
          id="cloud-create-server"
          className="form-stacked form-create"
          onSubmit={this.props.form.handleSubmit(this.handleSubmit)}
        >
          <div>
            {this.props.form.error && (
              <Base marginBottom="base">
                <Alert message={this.props.form.error} intent="danger" />
              </Base>
            )}
            <RadioButtonGroupField
              full
              help={__(
                'The displayed prices are based on a 30 day usage ' +
                  'and include all applicable taxes.',
              )}
              items={this.templatesItems}
              label={__('Specifications')}
              name="template"
              required
              validate={this.validateTemplate}
            />
            <RadioButtonGroupField
              full
              help={__(
                'We recommend selecting the datacenter close to your ' +
                  'users to get the best response time. If you use VLAN you ' +
                  'should store all your servers in the same datacenter.',
              )}
              items={this.datacentersItems}
              label={__('Location')}
              name="datacenter"
              required
              validate={this.validateDatacenter}
            />
            <RadioButtonGroupField
              required
              name="system"
              label={__('Operating System')}
              items={this.systemsItems}
              validate={this.validateSystem}
              full
            />
            {!!(
              formValues &&
              formValues.system &&
              this.versionsItems.length > 1
            ) && (
              <RadioButtonGroupField
                required
                name="version"
                label={__('Version')}
                items={this.versionsItems}
                validate={this.validateVersion}
                full
              />
            )}
          </div>
          {this.props.isLoaded && !this.props.isFetching && (
            <Action
              type="submit"
              variant="block"
              icon="arrowRight"
              form="cloud-create-server"
              loading={this.props.cartIsWorking}
              disabled={!this.props.form.valid}
            >
              {__('Next')}
            </Action>
          )}
        </Form>
      );
    }

    return null;
  }
}
